import { applyPageShellBg, resolveShellBgFromPathname } from "./pageShellBg";

/** 追加到 H5 URL，供 H5 识别小程序 web-view 环境 */
export const MINIAPP_QUERY_FLAG = "lfMiniapp";
export const MINIAPP_DEFAULT_NAV_TITLE = "龙粉社";
const MINIAPP_WEB_PAGE_PATH = "/pages/web/index";

export function isLfMiniProgramEnv(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  if (params.get(MINIAPP_QUERY_FLAG) === "1") {
    return true;
  }
  const wxEnv = (window as Window & { __wxjs_environment?: string }).__wxjs_environment;
  if (wxEnv === "miniprogram") {
    return true;
  }
  return /miniProgram/i.test(navigator.userAgent || "");
}

/** 从 H5 path（可含 query）提取 pathname */
export function extractPathnameFromH5Path(h5Path: string): string {
  const qIdx = h5Path.indexOf("?");
  return qIdx >= 0 ? h5Path.slice(0, qIdx) : h5Path;
}

/**
 * 小程序 bindload 等场景：是否为平台/空间首页（需同步壳页顶栏色）。
 */
export function isMiniProgramShellHomePath(h5Path: string): boolean {
  const pathname = extractPathnameFromH5Path(h5Path);
  if (!pathname || pathname === "/") {
    return true;
  }
  return /^\/space\/\d{8}\/home\/?$/.test(pathname);
}

/** 是否空间页路径 */
export function isSpacePath(h5Path: string): boolean {
  return /^\/space\/\d{8}\//.test(extractPathnameFromH5Path(h5Path));
}

/** 是否空间首页路径 */
export function isSpaceHomePath(h5Path: string): boolean {
  return /^\/space\/\d{8}\/home\/?$/.test(extractPathnameFromH5Path(h5Path));
}

/** 是否平台首页路径 */
export function isPlatformHomePath(h5Path: string): boolean {
  const pathname = extractPathnameFromH5Path(h5Path);
  return !pathname || pathname === "/";
}

/**
 * 需 redirectTo 重载 web-view 的场景：
 * 1. 平台首页 → 空间页（进入空间，同步顶栏）
 * 2. 空间首页 → 平台首页（菜单「回到平台首页」，同步黑色顶栏）
 * 其余（含空间内 Tab）走 SPA。
 */
export function shouldMiniProgramReloadWebView(targetPath: string): boolean {
  if (!isLfMiniProgramEnv()) {
    return false;
  }
  const from = window.location.pathname;
  if (from === "/" && isSpacePath(targetPath)) {
    return true;
  }
  if (isSpaceHomePath(from) && isPlatformHomePath(targetPath)) {
    return true;
  }
  return false;
}

type WxMiniProgramBridge = {
  miniProgram?: {
    redirectTo?: (options: {
      url: string;
      fail?: (err: unknown) => void;
    }) => void;
    reLaunch?: (options: {
      url: string;
      fail?: (err: unknown) => void;
    }) => void;
  };
};

function loadWxBridge(): Promise<WxMiniProgramBridge | undefined> {
  const w = window as Window & { wx?: WxMiniProgramBridge };
  if (w.wx?.miniProgram) {
    return Promise.resolve(w.wx);
  }
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js";
    script.onload = () => resolve((window as Window & { wx?: WxMiniProgramBridge }).wx);
    script.onerror = () => resolve(undefined);
    document.head.appendChild(script);
  });
}

function buildMiniProgramWebUrl(h5Path: string): string {
  const normalized = h5Path.startsWith("/") ? h5Path : `/${h5Path}`;
  return `${MINIAPP_WEB_PAGE_PATH}?path=${encodeURIComponent(normalized)}`;
}

/** 小程序内跳转：redirectTo 到 web 页，顶栏颜色由小程序 onLoad(path) 解析 */
export function miniProgramNavigate(path: string): void {
  void loadWxBridge().then((wx) => {
    const mpUrl = buildMiniProgramWebUrl(path);
    const go = wx?.miniProgram?.redirectTo ?? wx?.miniProgram?.reLaunch;
    if (!go || !wx?.miniProgram) {
      console.warn("[lf-h5][miniapp-nav] wx.miniProgram.redirectTo unavailable");
      return;
    }
    go.call(wx.miniProgram, {
      url: mpUrl,
      fail: (err) => console.error("[lf-h5][miniapp-nav] redirectTo fail", mpUrl, err),
    });
  });
}

/**
 * 小程序内：平台首页 ↔ 空间首页 需 redirectTo；其余（含空间内 Tab）走 SPA。
 * 浏览器内始终走 React Router。
 */
export function h5NavigateTo(
  navigate: (path: string, options?: { replace?: boolean }) => void,
  path: string,
  options?: { replace?: boolean }
): void {
  if (shouldMiniProgramReloadWebView(path)) {
    miniProgramNavigate(path);
    return;
  }
  navigate(path, options);
}

/** 小程序 web-view：标记环境 + 本地 body 背景 */
export function applyMiniProgramPageTheme(pathname: string): void {
  if (!isLfMiniProgramEnv()) {
    return;
  }
  document.documentElement.classList.add("lf-h5-in-miniapp");
  applyPageShellBg(resolveShellBgFromPathname(pathname));
}
